/**
 * Global error handling utilities
 */

/**
 * Handle unhandled promise rejections
 */
export const handleUnhandledRejection = (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  
  // Log to error reporting service if available
  // Example: logErrorToService(event.reason, 'unhandled_promise_rejection');
  
  // Prevent the default browser behavior
  event.preventDefault();
};

/**
 * Handle uncaught errors
 */
export const handleUncaughtError = (event) => {
  console.error('Uncaught error:', event.error);
  
  // Log to error reporting service if available
  // Example: logErrorToService(event.error, 'uncaught_error');
  
  // Prevent the default browser behavior
  event.preventDefault();
};

/**
 * Handle resource loading errors
 */
export const handleResourceError = (event) => {
  console.error('Resource loading error:', event);
  
  // Check if it's a manifest.json error
  if (event.target && event.target.src && event.target.src.includes('manifest.json')) {
    console.warn('Manifest.json loading failed - this is often caused by browser extensions or caching issues');
    // Don't prevent default for manifest errors as they're usually non-critical
    return;
  }
  
  // Log to error reporting service if available
  // Example: logErrorToService(event, 'resource_error');
};

/**
 * Initialize global error handlers
 */
export const initializeErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  
  // Handle uncaught errors
  window.addEventListener('error', handleUncaughtError);
  
  // Handle resource loading errors
  window.addEventListener('error', handleResourceError, true);
  
  console.log('Global error handlers initialized');
};

/**
 * Clean up error handlers
 */
export const cleanupErrorHandlers = () => {
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  window.removeEventListener('error', handleUncaughtError);
  window.removeEventListener('error', handleResourceError, true);
  
  console.log('Global error handlers cleaned up');
};

/**
 * Safe async function wrapper
 * @param {Function} fn - The async function to wrap
 * @param {*} defaultValue - Default value if function throws
 * @param {...*} args - Arguments to pass to the function
 * @returns {Promise} The function result or default value
 */
export const safeAsync = async (fn, defaultValue = null, ...args) => {
  try {
    if (typeof fn === 'function') {
      return await fn(...args);
    }
    return defaultValue;
  } catch (error) {
    console.warn('Safe async call failed:', error);
    return defaultValue;
  }
};

/**
 * Retry function with exponential backoff
 * @param {Function} fn - The function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {...*} args - Arguments to pass to the function
 * @returns {Promise} The function result
 */
export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000, ...args) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn(...args);
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`Attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Debounce function to prevent rapid successive calls
 * @param {Function} fn - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} The debounced function
 */
export const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Throttle function to limit function calls
 * @param {Function} fn - The function to throttle
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} The throttled function
 */
export const throttle = (fn, delay) => {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      return fn(...args);
    }
  };
};
